import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { dashboardApi } from "@/api/endpoints";
import type { AgencyCommandActionItem, AgencyCommandCenterResponse } from "@/api/types";
import { PageHeader } from "@/components/PageHeader";
import { StatCard } from "@/components/StatCard";
import { formatCurrency } from "@/utils/format";

function severityClass(severity: string): string {
  if (severity === "critical") return "action-severity-critical";
  if (severity === "warning") return "action-severity-warning";
  return "action-severity-info";
}

function ActionCard({ item }: { item: AgencyCommandActionItem }) {
  return (
    <Link to={item.href} className={`command-action-card ${severityClass(item.severity)}`}>
      <div className="command-action-head">
        <span className={`action-dot ${severityClass(item.severity)}`} aria-hidden />
        <strong>{item.title}</strong>
      </div>
      {item.subtitle ? <div className="command-action-subtitle">{item.subtitle}</div> : null}
      <p className="command-action-reason">{item.reason}</p>
    </Link>
  );
}

function ActionColumn({
  column,
}: {
  column: AgencyCommandCenterResponse["revenue_actions"];
}) {
  return (
    <section className="command-column card">
      <header className="command-column-header">
        <h3>{column.label}</h3>
        <span className="command-column-count">{column.count}</span>
      </header>
      {column.items.length === 0 ? (
        <p className="empty-state" style={{ padding: "0.75rem" }}>
          Nothing urgent here — good sign.
        </p>
      ) : (
        <div className="command-action-list">
          {column.items.map((item) => (
            <ActionCard key={item.id} item={item} />
          ))}
        </div>
      )}
    </section>
  );
}

export function DashboardPage() {
  const [data, setData] = useState<AgencyCommandCenterResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    dashboardApi
      .agencyCommandCenter()
      .then(setData)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load command center"));
  }, []);

  return (
    <>
      <PageHeader
        title="Agency Command Center"
        subtitle="What needs attention today — revenue, service, and money risks in one cockpit."
      />
      {error ? <div className="error-banner">{error}</div> : null}

      {!data ? (
        <div className="loading-screen" style={{ minHeight: "12rem" }}>
          <div className="spinner" />
        </div>
      ) : (
        <>
          <section className="stat-grid command-kpi-grid">
            <StatCard label="Active clients" value={data.kpis.active_clients} to="/clients" />
            <StatCard label="Open leads" value={data.kpis.open_leads} to="/pipeline" />
            <StatCard label="Renewals (90d)" value={data.kpis.renewals_90_days} to="/renewals" />
            <StatCard label="Open claims" value={data.kpis.open_claims} to="/claims" />
            <StatCard label="SLA at risk" value={data.kpis.sla_at_risk} to="/claims" />
            <StatCard
              label="Stuck quote premium"
              value={formatCurrency(data.kpis.pipeline_premium_cents)}
              to="/quotes"
            />
          </section>

          {data.priority_actions.length > 0 ? (
            <section className="card command-priority-panel">
              <header className="command-column-header">
                <h3>What your team should do today</h3>
                <span className="command-column-count">{data.priority_actions.length}</span>
              </header>
              <div className="command-priority-list">
                {data.priority_actions.map((item, index) => (
                  <Link key={item.id} to={item.href} className="command-priority-row">
                    <span className="command-priority-rank">{index + 1}</span>
                    <span className={`action-dot ${severityClass(item.severity)}`} aria-hidden />
                    <span className="command-priority-text">
                      <strong>{item.title}</strong>
                      <span>{item.reason}</span>
                    </span>
                  </Link>
                ))}
              </div>
            </section>
          ) : null}

          <div className="command-columns">
            <ActionColumn column={data.revenue_actions} />
            <ActionColumn column={data.service_risks} />
            <ActionColumn column={data.money_risks} />
          </div>
        </>
      )}
    </>
  );
}
